import { useEffect, useState } from "react"
import Page from "../../common/page/Page"
import axios from "axios"
import ButtonArea from "../../common/ButtonArea/ButtonArea"
import Form from "../../common/form/Form"
import ResponsiveTable from "../../common/ResponsiveTable/ResponsiveTable"
import EditIcon from "../../icons/EditIocn"
import ToggleOnIcon from "../../icons/ToggleOnIcon"
import ToggleOffIcon from "../../icons/ToggleOffIcon"
import Loader from "../../common/loader/Loader"
import KeyIcon from "../../icons/KeyIcon"
import { useFlashMessage } from "../../common/hook/useFlashMessage"
import FlashMessage from "../../common/FlashMesage/FlashMessage"
import { useAuth } from "../../data/useAuth"

// ✅ Static permission list
const STATIC_PERMISSIONS = [
  "product_manager",
  "category_manager",
  "delivery_manager",
  "hr",
]

const EmployeePage = () => {
  const { signOut } = useAuth()
  const { flash, showMessage, hideMessage } = useFlashMessage()
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [formActive, setFormActive] = useState(false)
  const [isAdd, setIsAdd] = useState(true)
  const [formMode, setFormMode] = useState("add-edit") // or "reset-password"

  const [formData, setFormData] = useState({
    id: null,
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password: "",
    permissions: [],
    new_password: "",
  })

  useEffect(() => {
    fetchEmployees()
  }, [])

  const fetchEmployees = async () => {
    try {
      const response = await axios.get("/api/employee/")
      setEmployees(response.data || [])
    } catch (err) {
      setError(err.response?.data?.error || err.message)
      signOut()
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { id, value } = e.target
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handlePermissionChange = (permission) => {
    setFormData((prev) => {
      const exists = prev.permissions.includes(permission)
      return {
        ...prev,
        permissions: exists
          ? prev.permissions.filter((p) => p !== permission)
          : [...prev.permissions, permission],
      }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setFormActive(false)
    try {
      if (formMode === "add-edit") {
        if (!formData.first_name.trim() || !formData.email.trim()) {
          showMessage({
            message: `First name and Email are required.`,
            type: "error",
            mode: "auto",
          })
          setLoading(false)
          setFormActive(true)
          return
        }
        if (isAdd) {
          await axios.post("/api/employee/add", formData)
        } else {
          await axios.put(`/api/employee/edit/${formData.id}`, formData)
        }
        await fetchEmployees()
      } else if (formMode === "reset-password") {
        if (!formData.new_password.trim()) {
          showMessage({
            message: `New password is required.`,
            type: "error",
            mode: "auto",
          })
          setLoading(false)
          setFormActive(true)
          return
        }
        await axios.patch(`/api/employee/reset-password/${formData.id}`, {
          password: formData.new_password,
        })
        showMessage({
          message: `Password updated successfully.`,
          type: "success",
          mode: "auto",
        })
      }

      setFormActive(false)
      resetForm()
      setLoading(false)
    } catch (err) {
      setLoading(false)
      setFormActive(true)
      setError(err.response?.data?.error || err.message)
    }
  }

  const resetForm = () => {
    setFormData({
      id: null,
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      password: "",
      permissions: [],
      new_password: "",
    })
    setIsAdd(true)
    setFormMode("add-edit")
  }

  const handleEdit = (emp) => {
    setFormActive(true)
    setIsAdd(false)
    setFormMode("add-edit")
    setFormData({
      id: emp.id,
      first_name: emp.first_name,
      last_name: emp.last_name,
      email: emp.email,
      phone_number: emp.phone_number,
      password: "",
      permissions: emp.Permissions?.map((p) => p.permission_name) || [],
      new_password: "",
    })
  }

  const handleResetPasswordForm = (emp) => {
    setFormActive(true)
    setIsAdd(false)
    setFormMode("reset-password")
    setFormData({
      id: emp.id,
      first_name: "",
      last_name: "",
      email: emp.email,
      phone_number: "",
      password: "",
      permissions: [],
      new_password: "",
    })
  }

  const handleActivate = async (id) => {
    try {
      await axios.patch(`/api/employee/activate/${id}`)
      await fetchEmployees()
      showMessage({
        message: `actuate user successfully`,
        type: "success",
        mode: "auto",
      })
    } catch (err) {
      showMessage({
        message: `Failed to activate user`,
        type: "error",
        mode: "auto",
      })
      console.error(err.response?.data?.error || "Failed to activate user")
    }
  }

  const handleDeactivate = async (id) => {
    try {
      await axios.patch(`/api/employee/deactivate/${id}`)
      await fetchEmployees()
      showMessage({
        message: `deactivate user successfully`,
        type: "success",
        mode: "auto",
      })
    } catch (err) {
      showMessage({
        message: `Failed to deactivate user`,
        type: "error",
        mode: "auto",
      })
      console.error(err.response?.data?.error || "Failed to deactivate user")
    }
  }

  return (
    <Page title={"Employee"} is_admin={true}>
      {!formActive && (
        <ButtonArea>
          <button
            onClick={() => {
              setFormActive(true)
              setIsAdd(true)
              resetForm()
            }}
          >
            Add Employee
          </button>
        </ButtonArea>
      )}

      {formActive && (
        <Form is_admin={true} onSubmit={handleSubmit}>
          {formMode === "add-edit" ? (
            <>
              <div>
                <div>
                  <label htmlFor="first_name">First Name</label>
                  <input
                    type="text"
                    id="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div>
                <div>
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    type="text"
                    id="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div>
                <div>
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    readOnly={!isAdd}
                  />
                </div>
              </div>
              <div>
                <div>
                  <label htmlFor="phone_number">Phone Number</label>
                  <input
                    type="text"
                    id="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              {isAdd && (
                <div>
                  <div>
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              )}
              <div>
                <label>Permissions</label>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {STATIC_PERMISSIONS.map((perm) => (
                    <label
                      key={perm}
                      style={{ marginRight: "10px", userSelect: "none" }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(perm)}
                        onChange={() => handlePermissionChange(perm)}
                      />{" "}
                      {perm}
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div>
              <div>
                <label htmlFor="new_password">New Password</label>
                <input
                  type="password"
                  id="new_password"
                  value={formData.new_password}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          )}

          <ButtonArea>
            <button type="submit">
              {formMode === "add-edit"
                ? isAdd
                  ? "Add Employee"
                  : "Update Employee"
                : "Update Password"}
            </button>
            <button
              type="button"
              onClick={() => {
                setFormActive(false)
                resetForm()
              }}
            >
              Close
            </button>
          </ButtonArea>
        </Form>
      )}

      {loading && (
        <div style={{ height: "calc(100svh - 100px)", display: "flex" }}>
          <div style={{ margin: "auto" }}>
            <Loader />
          </div>
        </div>
      )}

      {!loading && !error && !formActive && (
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Id</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Permissions</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.id}>
                <td data-label="Id">{emp.id}</td>
                <td data-label="Name">{emp.first_name}</td>
                <td data-label="Email">{emp.email}</td>
                <td data-label="Phone">{emp.phone_number}</td>
                <td data-label="Permissions" style={{ maxWidth: "120px" }}>
                  {emp.Permissions?.map((p) => p.permission_name).join(", ")}
                </td>
                <td data-label="Status">
                  <button
                    onClick={() =>
                      emp.is_active
                        ? handleDeactivate(emp.id)
                        : handleActivate(emp.id)
                    }
                    style={{
                      marginLeft: "5px",
                      padding: "3px 6px",
                      fill: emp.is_active ? "green" : "red",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {emp.is_active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                  </button>
                  {/* {emp.is_active ? (
                    <span style={{ color: "green" }}>Active</span>
                  ) : (
                    <span style={{ color: "red" }}>Inactive</span>
                  )} */}
                </td>
                <td data-label="Actions">
                  <button
                    onClick={() => handleEdit(emp)}
                    style={{
                      padding: "3px 6px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                    }}
                  >
                    <EditIcon />
                  </button>
                  <button
                    onClick={() => handleResetPasswordForm(emp)}
                    style={{
                      padding: "3px 6px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                    }}
                  >
                    <KeyIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
      {flash && (
        <FlashMessage
          message={flash.message}
          type={flash.type}
          mode={flash.mode}
          onClose={hideMessage}
        />
      )}
    </Page>
  )
}

export default EmployeePage
